import * as Sentry from "@sentry/react";

import { ApiError } from "./ApiError";
import { getMode, MODE } from "./env-mode";
import { getSentryLevel, SENTRY_WARNING } from "./getSentryLevel";

const isDevelopment = () => {
  const mode = getMode();
  return mode === MODE.DEVELOPMENT || mode === MODE.TEST;
};

const buildErrorDetails = (
  endpoint: string,
  status: number,
  message: string,
): { title: string; summary: string } => {
  const summary = `API Error: ${endpoint} → ${status}`;
  return { title: message, summary };
};

/**
 * Reports an ApiError to Sentry with severity routing:
 * - 4xx → `warning` event with title/summary on the exception value
 * - 5xx and unknown → `error` exception captured via withScope
 *
 */
export const captureApiError = (
  error: ApiError,
  extraContext?: Record<string, unknown>,
): void => {
  try {
    const errorClass = error.errorClass;
    const level = getSentryLevel(errorClass);
    const { title, summary } = buildErrorDetails(
      error.endpoint,
      error.status,
      error.message,
    );

    const apiErrorContext = {
      http_code: error.status,
      endpoint: error.endpoint,
      response_body: error.responseBody ?? null,
      class: errorClass,
    };

    if (isDevelopment()) {
      console.log(
        `[Sentry ${level.toUpperCase()}] ${title} — ${summary}`,
        apiErrorContext,
        extraContext,
      );
      return;
    }

    if (level === SENTRY_WARNING) {
      Sentry.captureEvent({
        level: SENTRY_WARNING,
        exception: {
          values: [
            {
              type: title,
              value: summary,
              mechanism: { handled: true, type: "generic" },
            },
          ],
        },
        contexts: { api_error: apiErrorContext },
        extra: extraContext,
      });
      return;
    }

    Sentry.withScope((scope) => {
      scope.setContext("api_error", apiErrorContext);
      scope.setExtra("summary", summary);
      if (extraContext) {
        Object.entries(extraContext).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureException(error);
    });
  } catch (err) {
    if (isDevelopment()) {
      console.error("[captureApiError] internal error:", err);
    }
  }
};
